import React, { useCallback, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import useWidgetFilterNavigator from "../../../../useWidgetFilterNavigator";
import TopFeatureTable, {
  BaseTopFeatureRow,
  WidgetTableProps,
} from "../TopFeatureTable";
import { GET_TOP_REPOS_DATA } from "../../../../WidgetDataApi";
import useQueryWithRetries from "../../../../../Tools/useQueryWithRetries";
import {
  Tooltip,
  useIcon,
  TableCell,
  tableCellTypesEnum,
} from "@SeemplicityDev/shared";

interface TopRepositoryRow extends BaseTopFeatureRow {
  repository: string;
  provider: string;
}

const TopRepositoriesTable: React.FC<WidgetTableProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_TOP_REPOS_DATA,
    variables: {
      widget_definition_key: "top_10_vulnerable_repositories",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id ?? null,
  });

  const onChartClick = useCallback(
    (severity: string, { repository, provider }: TopRepositoryRow) => {
      onFilterClick(
        [["in", "severity", severity]],
        [
          ["in", "cloud_provider", provider],
          ["in", "resource_name", repository],
        ]
      );
    },
    [filterToExecute, scopeToExecute, selectedScope]
  );

  const columnHelper = createColumnHelper<TopRepositoryRow>();

  const { getProviderIcon } = useIcon(isForExport ? "LIGHT" : undefined);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("repository", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div className="repository-cell">
              <Tooltip content={props.row.original.provider}>
                <img
                  className={"repository-icon"}
                  alt={props.row.original.provider}
                  src={
                    getProviderIcon(props.row.original.provider) || undefined
                  }
                />
              </Tooltip>
              <Tooltip content={props.getValue()}>
                <div className={"repository-text"}>{props.getValue()}</div>
              </Tooltip>
            </div>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Repository",
        minSize: 250,
      }),
    ];
  }, [getProviderIcon]);

  return (
    <TopFeatureTable
      data={data}
      columns={columns}
      onChartClick={onChartClick}
      loading={loading}
      error={error}
      refetch={refetch}
      id={id}
      isForExport={isForExport}
      setTotalWidgets={setTotalWidgets}
      index={index}
      identifier={"repository"}
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default TopRepositoriesTable;
